import moment from 'moment-timezone';
import { useCallback, useState } from 'react';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import {
  usePromoCodeInputContext,
} from 'Context/PromoCodeInputContext';

export function useSubmitHandler({ stripe, onSubmitEvent = () => {}, onErrorEvent = () => {} }) {
  const { promoCodeInputIsFilled } = usePromoCodeInputContext();
  const { rootNodeProps, logEvent } = useEventTrackingLogEvent({ nodeName: 'payment' });
  const [stripeError, setStripeError] = useState(undefined);
  const [stripeLoading, setStripeLoading] = useState(false);
  const {
    pageContentResource, submitPageResource, setPageContentCollection, isPageContentLoading,
  } = usePageContentContext();
  const {
    onSubmit: metaSubmit,
    saving: metaSaving,
    handleChange: metaChange,
    errors: metaErrors,
  } = useMetaLayoutContext();

  const onSubmit = useCallback(async () => {
    onSubmitEvent();
    setStripeError(undefined);

    // Call stripe.createToken, allowing for an error to be returned or thrown
    setStripeLoading(true);
    let result = {};
    try {
      result = await stripe.createToken();
    } catch (err) {
      result.error = err;
    }
    setStripeLoading(false);

    if (result.error) {
      onErrorEvent();
      setStripeError(result.error.message ?? result.error);
      return;
    }

    metaChange('stripeToken', result.token?.id);

    // None of the documentation explicitly says what this might return, so I'm wrapping this with `try` to avoid
    // causing any errors on the payment page.  If it's empty, the server will default it to EST.
    try {
      const tz = moment.tz.guess(true) || '';
      metaChange('timeZone', tz);
      metaChange('timeZoneAbbreviation', moment.tz(tz).zoneAbbr() || '');
    } catch (_) {} // eslint-disable-line

    await metaSubmit();
  }, [stripe, metaSubmit, metaChange, pageContentResource, submitPageResource, setPageContentCollection, logEvent]);

  return {
    rootNodeProps,
    onSubmit,
    stripeError,
    metaErrors,
    loading: metaSaving || stripeLoading,
    disabled: promoCodeInputIsFilled || pageContentResource.attr('memberAgreement') !== 'true' || pageContentResource.attr('termsAndConditions') !== 'true' || isPageContentLoading,
  };
}
